import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, List, ListOrdered, Quote, Heading1, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Undo2, Redo2,
} from "lucide-react";

/**
 * Reusable WYSIWYG. Returns HTML; pass `value` (HTML string) + `onChange`.
 * Supports headings (H1/H2/H3), bold, italic, lists, ordered lists,
 * blockquote, inline images (uploaded to /api/upload/image), links.
 */
export default function RichTextEditor({ value, onChange, placeholder, testid }) {
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#C9A055] underline underline-offset-2" } }),
      Image.configure({ HTMLAttributes: { class: "my-4 max-w-full border border-white/10" } }),
      Placeholder.configure({ placeholder: placeholder || "Start writing…" }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-rte min-h-[180px] focus:outline-none px-4 py-3 text-[#E8E4DC] font-body text-[15px] leading-[1.7]",
        "data-testid": testid || "rte",
      },
    },
  });

  // Keep editor in sync when `value` is replaced externally (e.g., when switching pages)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return <div className="border border-white/10 bg-[#080808] text-[#8a857d] text-sm p-4">Loading editor…</div>;

  const uploadImage = async (file) => {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
          res(data.url);
        } catch (e) { rej(e); }
      };
      reader.readAsDataURL(file);
    });
  };

  const onPickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const url = await uploadImage(f);
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const full = url.startsWith("/api/") ? `${base}${url}` : url;
      editor.chain().focus().setImage({ src: full, alt: f.name }).run();
    } catch { toast.error("Image upload failed."); }
    e.target.value = "";
  };

  const askLink = () => {
    const url = prompt("Enter link URL (use full https:// or relative /path):", "");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const Btn = ({ on, active, label, children, testid }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); on(); }}
      className={`w-8 h-8 flex items-center justify-center border transition-colors ${active ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#B5B0A7] hover:border-[#C9A055] hover:text-[#C9A055]"}`}
      aria-label={label}
      data-testid={testid}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-white/10 bg-[#080808] focus-within:border-[#C9A055]/60 transition-colors" data-testid={`${testid || "rte"}-wrapper`}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-white/10 bg-[#0a0a0a]">
        <Btn label="H1" on={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} testid="rte-h1"><Heading1 size={14}/></Btn>
        <Btn label="H2" on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} testid="rte-h2"><Heading2 size={14}/></Btn>
        <Btn label="H3" on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} testid="rte-h3"><Heading3 size={14}/></Btn>
        <span className="w-px h-5 bg-white/10 mx-0.5"/>
        <Btn label="Bold" on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} testid="rte-bold"><Bold size={13}/></Btn>
        <Btn label="Italic" on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} testid="rte-italic"><Italic size={13}/></Btn>
        <Btn label="Bulleted list" on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} testid="rte-ul"><List size={14}/></Btn>
        <Btn label="Numbered list" on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} testid="rte-ol"><ListOrdered size={14}/></Btn>
        <Btn label="Quote" on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} testid="rte-quote"><Quote size={13}/></Btn>
        <span className="w-px h-5 bg-white/10 mx-0.5"/>
        <Btn label="Link" on={askLink} active={editor.isActive("link")} testid="rte-link"><LinkIcon size={13}/></Btn>
        <Btn label="Image" on={() => fileRef.current?.click()} active={false} testid="rte-image"><ImageIcon size={13}/></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage}/>
        <span className="ml-auto flex gap-1">
          <Btn label="Undo" on={() => editor.chain().focus().undo().run()} testid="rte-undo"><Undo2 size={13}/></Btn>
          <Btn label="Redo" on={() => editor.chain().focus().redo().run()} testid="rte-redo"><Redo2 size={13}/></Btn>
        </span>
      </div>
      <EditorContent editor={editor}/>
    </div>
  );
}
